import { Component } from '@theme/component';

/**
 * @typedef {Object} SfMegaMenuRefs
 * @property {HTMLElement[]} panel - Mega menu panels, each with a `data-menu-title` attribute
 */

/**
 * Mega menu component for Sterling Forever.
 *
 * Manages hover-activated dropdown panels with open/close delays,
 * height animation, keyboard dismiss, and click-outside closing.
 *
 * @extends {Component<SfMegaMenuRefs>}
 */
class SfMegaMenu extends Component {
  /** @type {number | null} */
  #openDelay = null;

  /** @type {number | null} */
  #closeDelay = null;

  /** @type {HTMLElement | null} */
  #activePanel = null;

  /** @type {HTMLElement | null} */
  #activeTrigger = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('keydown', this.#handleKeydown);
    document.addEventListener('click', this.#handleClickOutside);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('keydown', this.#handleKeydown);
    document.removeEventListener('click', this.#handleClickOutside);
    this.#clearTimers();
  }

  /**
   * Activates a mega menu panel after a short delay.
   * Called via `on:pointerenter="/activate"` on menu triggers.
   *
   * @param {PointerEvent} event
   */
  activate(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const trigger = event.target.closest('[aria-expanded]') ?? event.target;
    if (!(trigger instanceof HTMLElement)) return;

    const title = (trigger.textContent ?? '').trim().toLowerCase();

    const panel = this.#findPanel(title);
    if (!panel) return;

    this.#clearClose();

    if (panel === this.#activePanel) return;

    this.#openDelay = window.setTimeout(() => {
      this.#openDelay = null;
      this.#openPanel(panel, trigger);
    }, 80);
  }

  /**
   * Schedules panel deactivation after a delay.
   * Called via `on:pointerleave="/deactivate"` on menu triggers and panels.
   *
   * @param {PointerEvent} _event
   */
  deactivate(_event) {
    this.#clearOpen();

    this.#closeDelay = window.setTimeout(() => {
      this.#closeDelay = null;
      this.#closeActivePanel();
    }, 300);
  }

  /**
   * Cancels a pending close when the pointer enters the panel.
   * Called via `on:pointerenter="/cancelClose"` on panels.
   */
  cancelClose() {
    this.#clearClose();
  }

  /**
   * Handles keydown on menu triggers. Escape closes the mega menu.
   *
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closeAll();
    }
  }

  /**
   * Immediately closes any open panel. Used for Escape key and external calls.
   */
  closeAll() {
    this.#clearTimers();
    this.#closeActivePanel();
  }

  /**
   * Opens a panel: sets ARIA states, measures height, and dispatches the open event.
   *
   * @param {HTMLElement} panel
   * @param {HTMLElement} trigger
   */
  #openPanel(panel, trigger) {
    if (this.#activePanel && this.#activePanel !== panel) {
      this.#closeActivePanel();
    }

    this.#activePanel = panel;
    this.#activeTrigger = trigger;

    trigger.setAttribute('aria-expanded', 'true');
    panel.setAttribute('aria-hidden', 'false');
    this.dataset.open = 'true';

    requestAnimationFrame(() => {
      const height = panel.scrollHeight;
      this.style.setProperty('--sf-submenu-height', `${height}px`);
    });

    document.dispatchEvent(new CustomEvent('sf-mega-menu:open'));
  }

  /**
   * Closes the currently active panel and dispatches the close event.
   */
  #closeActivePanel() {
    if (!this.#activePanel) return;

    if (this.#activeTrigger) {
      this.#activeTrigger.setAttribute('aria-expanded', 'false');
    }

    this.#activePanel.setAttribute('aria-hidden', 'true');
    this.#activePanel = null;
    this.#activeTrigger = null;
    this.dataset.open = 'false';

    this.style.setProperty('--sf-submenu-height', '0px');

    document.dispatchEvent(new CustomEvent('sf-mega-menu:close'));
  }

  /**
   * Finds a panel whose `data-menu-title` matches the given title (case-insensitive).
   *
   * @param {string} title
   * @returns {HTMLElement | null}
   */
  #findPanel(title) {
    if (!this.refs.panel) return null;

    const panels = Array.isArray(this.refs.panel) ? this.refs.panel : [this.refs.panel];
    return panels.find((p) => (p.dataset.menuTitle ?? '').toLowerCase() === title) ?? null;
  }

  /**
   * Closes all panels when the Escape key is pressed.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key === 'Escape') {
      this.closeAll();
    }
  };

  /**
   * Closes all panels when the user clicks outside the mega menu and its triggers.
   *
   * @param {MouseEvent} event
   */
  #handleClickOutside = (event) => {
    if (!this.#activePanel) return;
    if (!(event.target instanceof Node)) return;

    if (this.contains(event.target)) return;

    const header = this.closest('sf-header');
    if (header && header.contains(event.target)) return;

    this.closeAll();
  };

  /**
   * Cancels any pending open timeout.
   */
  #clearOpen() {
    if (this.#openDelay !== null) {
      clearTimeout(this.#openDelay);
      this.#openDelay = null;
    }
  }

  /**
   * Cancels any pending close timeout.
   */
  #clearClose() {
    if (this.#closeDelay !== null) {
      clearTimeout(this.#closeDelay);
      this.#closeDelay = null;
    }
  }

  /**
   * Cancels both open and close timeouts.
   */
  #clearTimers() {
    this.#clearOpen();
    this.#clearClose();
  }
}

if (!customElements.get('sf-mega-menu')) {
  customElements.define('sf-mega-menu', SfMegaMenu);
}
